'use client';

import { useMemo } from 'react';
import { useSafeFeatureFlagPayload } from 'hooks/useSafeFeatureFlag';
import type { OfferingCardData, OfferingSection } from './demoData';

/**
 * Per-offering eligibility labels configured by operations in PostHog.
 * The payload is a case-insensitive offering slug -> badge type map, e.g.
 * `{ "openai-ira": "ira", "starcloud-qp-1": "qp" }`.
 */
export const ELIGIBILITY_BADGES_FLAG = 'se3-offering-eligibility-badges';

const ELIGIBILITY_BADGE_LABELS: Record<string, string> = {
  qp: 'Qualified Purchasers Exclusive',
  ira: 'IRA Exclusive',
  soldOut: 'Previously Sold Out',
};

export const parseEligibilityBadges = (payload: unknown): Record<string, string> => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {};

  const badges: Record<string, string> = {};
  Object.entries(payload as Record<string, unknown>).forEach(([slug, value]) => {
    const label = typeof value === 'string' ? ELIGIBILITY_BADGE_LABELS[value] : undefined;
    const normalizedSlug = slug.trim().toLowerCase();
    if (normalizedSlug && label) badges[normalizedSlug] = label;
  });
  return badges;
};

/** The PostHog-backed eligibility map used by every SE3 tombstone surface. */
export const useEligibilityBadges = (): Record<string, string> => {
  const payload = useSafeFeatureFlagPayload(ELIGIBILITY_BADGES_FLAG);
  return useMemo(() => parseEligibilityBadges(payload), [payload]);
};

/** Apply admin-configured labels without mutating unrelated card data. */
export const applyEligibilityBadges = (
  cards: OfferingCardData[],
  badges: Record<string, string>,
): OfferingCardData[] =>
  cards.map((card) => {
    const label = badges[card.co.toLowerCase()];
    return label ? { ...card, meta: label } : card;
  });

/**
 * Apply the admin payload at the section boundary so every consumer of the
 * shared tombstone results (homepage, Explore, and Private) gets identical
 * badge behavior, including static fallback data.
 */
export const applyEligibilityBadgesToSections = (
  sections: OfferingSection[],
  badges: Record<string, string>,
): OfferingSection[] => {
  if (Object.keys(badges).length === 0) return sections;

  return sections.map((section) => ({
    ...section,
    cards: applyEligibilityBadges(section.cards, badges),
  }));
};
